import { useSyncExternalStore } from "react";
import { api, ErrorApi } from "./api";
import { conexionCaida } from "./conexion";

/**
 * El mostrador sin internet.
 *
 * Si cae la WiFi del local, la tablet sigue vendiendo con lo que tiene
 * guardado —el «paquete»: socios con su límite y su consumo, productos y
 * precios— y apunta cada entrega en una cola que se envía sola al volver la
 * red. El servidor, al recibirla, la cobra con sus precios y la registra
 * siempre, aunque se hubiera pasado del límite o del stock: el género ya
 * salió del mostrador, y lo que se saltó queda escrito como incidencia.
 *
 * Todo vive en localStorage, no en memoria: una tablet se recarga, se le
 * acaba la batería, se cierra la pestaña. Una cola que se pierde con la
 * pestaña son ventas que no existen.
 *
 * Lo que se calcula aquí —límites, precios, saldo— es una vista previa con
 * los datos del último paquete más lo que hay en cola. El libro lo lleva el
 * servidor.
 */

const CLAVE_PAQUETE = "clubs.sinConexion.paquete";
const CLAVE_COLA = "clubs.sinConexion.cola";

/** El paquete caduca a los tres días: con datos más viejos, mejor no vender a ciegas. */
export const VIDA_DEL_PAQUETE_MS = 3 * 24 * 3600 * 1000;

export interface SocioDelPaquete {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  documentoNumero: string;
  estado: string;
  saldoCentimos: number;
  fotoUrl: string | null;
  fechaCaducidad: string | null;
  notas: string | null;
  codigoAcceso: string | null;
  tipoSocio: {
    id: string;
    nombre: string;
    color: string;
    cuotaAnualCentimos: number | null;
    descuentoPorcentaje: number;
  } | null;
  bloqueo: string | null;
  limiteDiarioMg: number;
  limiteMensualMg: number;
  consumidoHoyMg: number;
  consumidoMesMg: number;
}

export interface ProductoDelPaquete {
  id: string;
  nombre: string;
  genetica: string;
  precioCentimos: number;
  stockMg: number;
  thc: number | null;
  cbd: number | null;
  categoria: {
    id: string;
    nombre: string;
    color: string;
    unidadMedida: string;
  };
}

export interface PrecioDelPaquete {
  precioCentimos: number;
  precioBaseCentimos: number;
  descuentoTexto: string | null;
}

export interface Paquete {
  generadoEn: string;
  socios: SocioDelPaquete[];
  productos: ProductoDelPaquete[];
  /** Por tipo de socio (o `__ninguno__`), y dentro por producto. */
  precios: Record<string, Record<string, PrecioDelPaquete>>;
}

export interface EntregaEnCola {
  /** La clave con la que el servidor sabe que un envío repetido es la misma entrega. */
  clave: string;
  hechaEn: string;
  socioId: string;
  socioNombre: string;
  numeroSocio: number;
  lineas: {
    productoId: string;
    nombre: string;
    cantidadG: number;
    unidad: string;
  }[];
  metodoPago: string;
  forzarLimite: boolean;
  descuentoManual: { tipo: "PORCENTAJE" | "EUROS"; valor: number } | null;
  /** Lo que se cobró en el mostrador, con los precios del paquete. */
  totalCentimosPrevisto: number;
  totalMg: number;
  /**
   * Quien la hizo, que casi nunca es quien la enviara.
   *
   * La cola se vacia con la primera sesion que se abra despues, asi que sin
   * esto la entrega acaba firmada por quien no estaba. Vacio en las que ya
   * estaban en cola antes de que esto existiera: esas siguen firmandose con
   * quien las suba, como hasta ahora, porque no hay forma de saber mas.
   */
  hechaPor: { usuarioId: string; nombre: string } | null;
  /** Cuando el servidor la rechaza de plano (no por falta de red), el motivo. */
  error: string | null;
  intentos: number;
}

// --- Almacén -----------------------------------------------------------------

function leerJson<T>(clave: string): T | null {
  try {
    const texto = localStorage.getItem(clave);
    return texto ? (JSON.parse(texto) as T) : null;
  } catch {
    return null;
  }
}

function guardarJson(clave: string, valor: unknown): void {
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    // Sin sitio o sin almacenamiento: no hay nada mejor que hacer aquí.
  }
}

const oyentes = new Set<() => void>();
/** Sube con cada cambio: es lo que le dice a React que hay algo nuevo que leer. */
let version = 0;
function avisar() {
  version++;
  for (const oyente of oyentes) oyente();
}

export function leerPaquete(): Paquete | null {
  const paquete = leerJson<Paquete>(CLAVE_PAQUETE);
  if (!paquete) return null;
  if (Date.now() - new Date(paquete.generadoEn).getTime() > VIDA_DEL_PAQUETE_MS)
    return null;
  return paquete;
}

export function guardarPaquete(paquete: Paquete): void {
  guardarJson(CLAVE_PAQUETE, paquete);
  avisar();
}

/** Pide el paquete al servidor y lo guarda. Si no hay red, se queda el que había. */
export async function refrescarPaquete(): Promise<boolean> {
  try {
    guardarPaquete(await api<Paquete>("/dispensaciones/sin-conexion/paquete"));
    return true;
  } catch {
    return false;
  }
}

export function leerCola(): EntregaEnCola[] {
  return leerJson<EntregaEnCola[]>(CLAVE_COLA) ?? [];
}

function guardarCola(cola: EntregaEnCola[]): void {
  guardarJson(CLAVE_COLA, cola);
  avisar();
}

export function nuevaClave(): string {
  const azar =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `tablet-${azar}`;
}

export function encolar(
  entrega: Omit<EntregaEnCola, "clave" | "hechaEn" | "error" | "intentos"> & {
    clave?: string;
  },
): EntregaEnCola {
  const completa: EntregaEnCola = {
    ...entrega,
    // La clave puede venir puesta: es la de un envio que se corto a medias.
    clave: entrega.clave ?? nuevaClave(),
    hechaEn: new Date().toISOString(),
    error: null,
    intentos: 0,
  };
  guardarCola([...leerCola(), completa]);
  return completa;
}

export function quitarDeLaCola(clave: string): void {
  guardarCola(leerCola().filter((e) => e.clave !== clave));
}

// --- Trabajar con el paquete --------------------------------------------------

function sinAcentos(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Busca como el buscador del mostrador: por nombre, número, documento o el
 * código del carnet escaneado.
 */
export function buscarEnPaquete(
  paquete: Paquete,
  texto: string,
  maximo = 8,
): SocioDelPaquete[] {
  const t = sinAcentos(texto.trim());
  if (!t) return [];
  const numero = /^\d+$/.test(t) ? Number(t) : null;
  return paquete.socios
    .filter(
      (s) =>
        s.numeroSocio === numero ||
        s.codigoAcceso === texto.trim().toUpperCase() ||
        sinAcentos(`${s.nombre} ${s.apellidos}`).includes(t) ||
        sinAcentos(s.documentoNumero).includes(t),
    )
    .slice(0, maximo);
}

/** Lo que hay en cola de un socio, para que cuente en su límite y su cartera. */
function pendienteDe(socioId: string, cola: EntregaEnCola[]) {
  const hoy = new Date().toDateString();
  const mes = new Date().getMonth();
  let hoyMg = 0;
  let mesMg = 0;
  let centimosDeCartera = 0;
  for (const e of cola.filter((e) => e.socioId === socioId)) {
    const fecha = new Date(e.hechaEn);
    if (fecha.toDateString() === hoy) hoyMg += e.totalMg;
    if (fecha.getMonth() === mes) mesMg += e.totalMg;
    if (e.metodoPago === "CARTERA")
      centimosDeCartera += e.totalCentimosPrevisto;
  }
  return { hoyMg, mesMg, centimosDeCartera };
}

/**
 * Lo mismo que `GET /dispensaciones/preparar/:id`, calculado con el paquete y
 * con lo que hay en cola: lo que se vendió sin red cuenta para el límite y
 * baja la cartera, igual que si se hubiera enviado.
 */
export function prepararEnPaquete(
  paquete: Paquete,
  socioId: string,
  cola: EntregaEnCola[] = leerCola(),
) {
  const s = paquete.socios.find((x) => x.id === socioId);
  if (!s) return null;
  const pendiente = pendienteDe(socioId, cola);
  const consumidoHoyMg = s.consumidoHoyMg + pendiente.hoyMg;
  const consumidoMesMg = s.consumidoMesMg + pendiente.mesMg;
  const disponibleHoyMg = Math.max(0, s.limiteDiarioMg - consumidoHoyMg);
  const disponibleMesMg = Math.max(0, s.limiteMensualMg - consumidoMesMg);
  return {
    socio: {
      id: s.id,
      numeroSocio: s.numeroSocio,
      nombre: s.nombre,
      apellidos: s.apellidos,
      estado: s.estado,
      saldoCentimos: s.saldoCentimos - pendiente.centimosDeCartera,
      fotoUrl: s.fotoUrl,
      fechaCaducidad: s.fechaCaducidad,
      notas: s.notas,
      tipoSocio: s.tipoSocio,
    },
    consumo: {
      limiteDiarioMg: s.limiteDiarioMg,
      limiteMensualMg: s.limiteMensualMg,
      consumidoHoyMg,
      consumidoMesMg,
      disponibleMg: Math.min(disponibleHoyMg, disponibleMesMg),
    },
    bloqueo: s.bloqueo,
    dentro: false,
  };
}

/** Los precios de este socio, según su tipo. */
export function preciosEnPaquete(
  paquete: Paquete,
  socioId: string,
): Record<string, PrecioDelPaquete> {
  const s = paquete.socios.find((x) => x.id === socioId);
  return (
    paquete.precios[s?.tipoSocio?.id ?? "__ninguno__"] ??
    paquete.precios.__ninguno__ ??
    {}
  );
}

/** El stock que queda de cada producto, restando lo que hay en cola. */
export function productosEnPaquete(
  paquete: Paquete,
  cola: EntregaEnCola[] = leerCola(),
): ProductoDelPaquete[] {
  const enCola = new Map<string, number>();
  for (const e of cola) {
    for (const l of e.lineas)
      enCola.set(
        l.productoId,
        (enCola.get(l.productoId) ?? 0) + Math.round(l.cantidadG * 1000),
      );
  }
  return paquete.productos.map((p) => ({
    ...p,
    stockMg: p.stockMg - (enCola.get(p.id) ?? 0),
  }));
}

// --- Enviar la cola -----------------------------------------------------------

let enviando = false;

/**
 * Manda las entregas en cola, por orden, y quita las que entran.
 *
 * Si el servidor no está (fallo de red), se para y se vuelve a intentar
 * luego. Si el servidor contesta que no —un socio que ya no existe, una hora
 * imposible—, esa se queda en la cola con su motivo para que alguien decida:
 * no se descarta sola una venta.
 */
export async function enviarCola(): Promise<{
  enviadas: number;
  pendientes: number;
}> {
  if (enviando) return { enviadas: 0, pendientes: leerCola().length };
  enviando = true;
  let enviadas = 0;
  try {
    for (const entrega of leerCola()) {
      if (entrega.error) continue;
      try {
        await api("/dispensaciones", {
          metodo: "POST",
          cuerpo: {
            socioId: entrega.socioId,
            metodoPago: entrega.metodoPago,
            forzarLimite: entrega.forzarLimite,
            descuentoManual: entrega.descuentoManual,
            lineas: entrega.lineas.map((l) => ({
              productoId: l.productoId,
              cantidadG: l.cantidadG,
            })),
            claveLocal: entrega.clave,
            sinConexion: {
              hechaEn: entrega.hechaEn,
              // Con «?.» a proposito: las que se encolaron antes de que esto
              // existiera no lo traen, y el servidor las firma como siempre.
              usuarioId: entrega.hechaPor?.usuarioId,
            },
          },
        });
        quitarDeLaCola(entrega.clave);
        enviadas++;
      } catch (fallo) {
        const deRed = fallo instanceof ErrorApi && fallo.status === 0;
        if (deRed) {
          conexionCaida();
          break;
        }
        /*
         * Sesion caducada, cerrada desde Equipo o cuenta desactivada. No es un
         * problema de esta entrega, asi que no se aparta: se para el bucle y la
         * cola espera a que alguien entre. Apartarla significaria que, despues
         * de cerrar las sesiones, habria que ir reintentandolas una a una, y la
         * que se quedara olvidada es genero que salio del estante sin cobrar.
         *
         * Solo el 401. Un 403 -falta el permiso de dispensar- deja la sesion
         * viva y la cinta a la vista, asi que cae abajo y se apunta su motivo:
         * parar en silencio dejaria un «Enviar ahora» que no hace nada.
         */
        if (fallo instanceof ErrorApi && fallo.status === 401) break;
        const cola = leerCola();
        const propia = cola.find((e) => e.clave === entrega.clave);
        if (propia) {
          propia.intentos += 1;
          propia.error =
            fallo instanceof Error
              ? fallo.message
              : "El servidor no la ha aceptado";
          guardarCola(cola);
        }
      }
    }
  } finally {
    enviando = false;
  }
  if (enviadas > 0) void refrescarPaquete();
  return { enviadas, pendientes: leerCola().length };
}

/** Vuelve a intentar una que el servidor había rechazado. */
export function reintentar(clave: string): void {
  const cola = leerCola();
  const propia = cola.find((e) => e.clave === clave);
  if (propia) {
    propia.error = null;
    guardarCola(cola);
  }
}

// --- Para React -----------------------------------------------------------------

interface Instantanea {
  version: number;
  cola: EntregaEnCola[];
  paquete: Paquete | null;
}

let instantanea: Instantanea | null = null;

/* La misma instantanea mientras no cambie nada: useSyncExternalStore compara
   por identidad, y un objeto nuevo en cada lectura seria un bucle de pintado. */
function leerInstantanea(): Instantanea {
  if (!instantanea || instantanea.version !== version) {
    instantanea = { version, cola: leerCola(), paquete: leerPaquete() };
  }
  return instantanea;
}

function suscribir(oyente: () => void) {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

export function useSinConexion(): {
  cola: EntregaEnCola[];
  paquete: Paquete | null;
} {
  return useSyncExternalStore(suscribir, leerInstantanea, leerInstantanea);
}

/**
 * Tira la copia de socios que el mostrador guarda para trabajar sin red.
 *
 * Se llama al caer la sesion. La cola NO se toca: el paquete es una copia de
 * lo que ya esta en el servidor y se vuelve a bajar sola al volver a entrar,
 * pero la cola son entregas ya cobradas que el servidor todavia no ha visto.
 * Una se puede tirar y la otra es dinero.
 */
export function olvidarPaquete(): void {
  try {
    localStorage.removeItem(CLAVE_PAQUETE);
  } catch {
    // sin almacenamiento no hay nada que olvidar
  }
  avisar();
}

/** Para las pruebas. */
export function vaciarSinConexion(): void {
  try {
    localStorage.removeItem(CLAVE_PAQUETE);
    localStorage.removeItem(CLAVE_COLA);
  } catch {
    // sin almacenamiento no hay nada que vaciar
  }
  avisar();
}
